/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Text, rem, useMantineTheme, Menu } from '@mantine/core';
import {
  RiRestartLine,
  RiSettings3Fill,
  RiStopCircleFill,
  RiSunLine,
  RiCircleFill,
} from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useChangeFavicon } from '@fai/friendli-suite/features/common/hooks/use-change-favicon';
import {
  getFaviconTypeFromEndpointStatus,
  isEndpointAwakeable,
  isEndpointDeleteable,
  isEndpointRestartable,
  isEndpointStartable,
  isEndpointTerminateable,
  isEndpointUpdateCancelable,
  isEndpointUpdatePendingCancelable,
  isEndpointUpdateable,
} from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/endpoint-status';
import { useEndpointCancelUpdate } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-cancel-update';
import { useRestartEndpoint } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-restart';
import { useEndpointStatus } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-status';
import { useEndpointVersionInfo } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/versions/use-endpoint-version-info';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { useFlags } from '@fai/friendli-suite/shared/launch-darkly';
import TransparentBackLinkButton from '@fai/friendli-suite/views/common/button/back-button/transparent-link';
import EndpointTag from '@fai/friendli-suite/views/dedicated-endpoints/projects/endpoints/endpoint-tag';
import DEProjectSubHeader from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/sub-layout/header';
import Tag from '@fai/ui/components/tag';
import { TextVariants } from '@fai/ui/components/typography/variants';

import { openCancelPendingUpdateModal } from './cancel-pending-update-modal';
import { openDeleteEndpointModal } from './delete-endpoint-modal';
import { openRenameEndpointModal } from './rename-endpoint-modal';
import { openTerminateEndpointModal } from './terminate-endpoint-modal';
import { useAwakeEndpoint, useEndpointDetailHeader } from './use-endpoint-detail-header';

export default function EndpointDetailHeader() {
  const { t } = useTranslation();
  const { teamId = '', projectId = '', endpointId = '' } = useParams();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { dedicatedEndpointVersions } = useFlags();

  const { data } = useEndpointDetailHeader();
  const { status } = useEndpointStatus();
  const { currentVersion, isUpdateable, loading: versionInfoLoading } = useEndpointVersionInfo();

  useChangeFavicon(getFaviconTypeFromEndpointStatus(status));

  const { restartEndpoint, loading: restartLoading } = useRestartEndpoint();
  const { awakeEndpoint, loading: awakeLoading } = useAwakeEndpoint();
  const { endpointCancelUpdate: cancelUpdateEndpoint, loading: cancelUpdateLoading } =
    useEndpointCancelUpdate();

  const endpointName = data.name ?? '(name)';
  const endpointPath = Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).endpoint;

  const navigateToUpdateEndpoint = () => navigate(endpointPath.detail(endpointId).update);

  const startButton = (
    <Button theme="cloud" size="sm" loading={restartLoading} onClick={restartEndpoint}>
      {t('dedicated_endpoints.project.start_endpoint')}
    </Button>
  );

  const restartButton = (
    <Button
      variant="pill"
      size="sm"
      leftSection={<RiRestartLine size={16} />}
      loading={restartLoading}
      onClick={restartEndpoint}
    >
      {t('shared.restart')}
    </Button>
  );

  const awakeButton = (
    <Button
      variant="pill"
      size="sm"
      leftSection={<RiSunLine size={16} />}
      loading={awakeLoading}
      onClick={awakeEndpoint}
    >
      {t('dedicated_endpoints.project.awake')}
    </Button>
  );

  const terminateButton = (
    <Button
      variant="outline"
      size="sm"
      leftSection={<RiStopCircleFill size={16} />}
      onClick={openTerminateEndpointModal}
    >
      {t('dedicated_endpoints.project.terminate')}
    </Button>
  );

  const updateCancelButton = (
    <Button
      theme="danger"
      variant="outline"
      size="sm"
      loading={cancelUpdateLoading}
      onClick={cancelUpdateEndpoint}
    >
      {t('dedicated_endpoints.project.cancel_update')}
    </Button>
  );

  const updateButton =
    isEndpointUpdatePendingCancelable(status) && !isUpdateable ? (
      <Menu position="bottom-end">
        <Menu.Target>
          <Button
            variant="outline"
            size="sm"
            leftSection={<RiSettings3Fill size={16} />}
            rightSection={
              <RiCircleFill color={theme.pf.color.fg.cloudblue.default.base} size={8} />
            }
            loading={versionInfoLoading}
          >
            {t('dedicated_endpoints.project.update')}
          </Button>
        </Menu.Target>

        <Menu.Dropdown miw={rem(160)} style={{ boxShadow: theme.pf.shadow['shadow-md'] }}>
          {isEndpointAwakeable(status) && (
            <Menu.Item onClick={awakeEndpoint}>
              <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.BodyMd}>
                {t('dedicated_endpoints.project.awake_to_update')}
              </Text>
            </Menu.Item>
          )}

          <Menu.Item onClick={openCancelPendingUpdateModal}>
            <Text c={theme.pf.color.fg.danger.default.base} variant={TextVariants.BodyMd}>
              {t('dedicated_endpoints.project.cancel_pending_update')}
            </Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ) : (
      <Button
        variant="outline"
        size="sm"
        leftSection={<RiSettings3Fill size={16} />}
        disabled={!isEndpointUpdateable(status)}
        loading={versionInfoLoading}
        onClick={navigateToUpdateEndpoint}
      >
        {t('dedicated_endpoints.project.update')}
      </Button>
    );

  const EllipsisMenu = (
    <DEProjectSubHeader.EllipsisMenu>
      <DEProjectSubHeader.EllipsisMenuItem
        onClick={() => openRenameEndpointModal({ endpointName })}
      >
        {t('shared.rename')}
      </DEProjectSubHeader.EllipsisMenuItem>

      <DEProjectSubHeader.EllipsisMenuItem
        c={theme.pf.color.fg.danger.default.base}
        disabled={!isEndpointDeleteable(status)}
        onClick={() => openDeleteEndpointModal({ endpointName })}
      >
        {t('shared.delete')}
      </DEProjectSubHeader.EllipsisMenuItem>
    </DEProjectSubHeader.EllipsisMenu>
  );

  return (
    <DEProjectSubHeader
      titleSlot={
        <DEProjectSubHeader.TitleRow
          title={endpointName}
          titleSuffixSlot={
            dedicatedEndpointVersions && (
              <Tag bg={theme.pf.color.fg.white}>{currentVersion?.version ?? '-'}</Tag>
            )
          }
          leftSlot={
            <>
              <EndpointTag showPhase={true} />
              {isEndpointStartable(status) && startButton}
              {isEndpointAwakeable(status) && awakeButton}
              {isEndpointRestartable(status) && restartButton}
            </>
          }
          rightSlot={
            <>
              {isEndpointTerminateable(status) && terminateButton}
              {isEndpointUpdateCancelable(status) && updateCancelButton}
              {isEndpointUpdateable(status) && updateButton}
              {EllipsisMenu}
            </>
          }
        />
      }
      backButtonSlot={
        <TransparentBackLinkButton to={endpointPath.list}>
          {t('dedicated_endpoints.project.back_to_endpoints')}
        </TransparentBackLinkButton>
      }
    />
  );
}

EndpointDetailHeader.displayname = 'EndpointDetailHeader';
